<template>
  <div class="billing-group-card">
    <!-- Header: bisa diklik untuk collapse/expand -->
    <div
      class="group-header"
      :class="[{ 'mb-4': !isCollapsed, 'border-0': isCollapsed }]"
      @click="toggleCollapse"
    >
      <button
        type="button"
        class="collapse-btn"
        :class="{ 'is-collapsed': isCollapsed }"
        :aria-expanded="(!isCollapsed).toString()"
        aria-label="Toggle group"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path
            d="M6 9l6 6 6-6"
            stroke="#2E3646"
            stroke-width="2.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <span class="header-text">
        {{ formatDate(group.startRent) }} - {{ formatDate(group.endRent) }} ·
        {{ group.duration }} bulan · {{ formatRupiah(group.price) }}/unit
      </span>

      <span
        class="unit-badge"
        :class="{ 'unit-badge--warning': lateWarningDays !== 0 }"
      >
        {{ group.listItem.length }} unit
      </span>
    </div>

    <!-- Body: disembunyikan saat collapsed -->
    <div v-show="!isCollapsed" class="group-body">
      <div v-if="lateWarningDays !== 0" class="warning-banner">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          class="warning-icon"
        >
          <path
            d="M12 9v4M12 17h.01M10.29 3.86l-8.18 14.18A2 2 0 0 0 3.82 21h16.36a2 2 0 0 0 1.71-2.96L13.71 3.86a2 2 0 0 0-3.42 0z"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span
          >Grup ini mulai {{ Math.abs(lateWarningDays) }} hari lebih
          {{ lateWarningDays > 0 ? "lambat" : "cepat" }} dari grup di
          atas.</span
        >
      </div>

      <div class="unit-list-label mt-3">Unit dalam grup ini:</div>
      <div class="unit-tags">
        <span
          v-for="(unit, idx) in group.listItem"
          :key="idx"
          class="unit-tag"
          >{{ unit }}</span
        >
      </div>

      <div class="table-label">
        Rincian tanggal tagihan (berlaku untuk semua unit di grup ini):
      </div>

      <div v-if="group.schema.length" class="payment-table">
        <!-- Header -->
        <div class="payment-row payment-header">
          <div>PERIODE DARI</div>
          <div>PERIODE SAMPAI</div>
          <div>PEMBAYARAN KE</div>
          <div>TANGGAL TAGIHAN</div>
          <div>NILAI</div>
        </div>

        <!-- Rows -->
        <div
          v-for="(item, index) in schemaRows"
          :key="index"
          class="payment-row payment-row-content"
        >
          <template v-if="item.isEllipsis">
            <div
              v-for="(val, idx) in Array(5)"
              :key="idx"
              class="ellipsis-dots"
            >
              ...
            </div>
          </template>
          <template v-else>
            <div>
              {{ formatDate(new Date(item.periodFrom)) }}
            </div>

            <div>
              {{ formatDate(new Date(item.periodTo)) }}
            </div>

            <div class="payment-number">
              {{ item.paymentNumber }}{{ item.isProrate ? " - Prorate" : "" }}
            </div>

            <div>
              {{ formatDate(new Date(item.invoiceDate)) }}
            </div>

            <div>
              {{ formatRupiah(item.amount) }}
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PaymentGroup",

  props: {
    group: {
      type: Object,
      required: true,
    },
    // Apakah collapsed di awal
    defaultCollapsed: {
      type: Boolean,
      default: false,
    },
    // Berapa banyak baris tabel ditampilkan sebelum "..." (baris pertama & terakhir selalu tampil)
    maxVisibleRows: {
      type: Number,
      default: 5,
    },
    prevDataStartRent: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      isCollapsed: this.defaultCollapsed,
    };
  },

  computed: {
    lateWarningDays() {
      if (!this.prevDataStartRent) return 0;

      const prevDate = new Date(this.prevDataStartRent);
      const currentDate = new Date(this.group.startRent);

      const diffTime = currentDate - prevDate;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return diffDays;
    },
    schemaRows() {
      if (!this.group.schema) return [];
      if (this.group.schema.length > 4) {
        return [
          ...this.group.schema.slice(0, 3),
          {
            isEllipsis: true,
          },
          this.group.schema[this.group.schema.length - 1],
        ];
      }
      return this.group.schema || [];
    },
  },

  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      this.$emit("toggle", this.isCollapsed);
    },
    formatDate(val) {
      const date = new Date(val);
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "Mei",
        "Jun",
        "Jul",
        "Agu",
        "Sep",
        "Okt",
        "Nov",
        "Des",
      ];

      return `${date.getDate()} ${
        months[date.getMonth()]
      } ${date.getFullYear()}`;
    },
    formatRupiah(value) {
      return "Rp " + new Intl.NumberFormat("id-ID").format(value);
    },
  },
};
</script>

<style scoped>
.billing-group-card {
  border: 1px solid #e2e5ea;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
}

/* ---------- Header ---------- */
.group-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid #d1d9e2;
}

.collapse-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: #6b7280;
  padding: 0;
  cursor: pointer;
  transition: transform 0.18s ease;
  flex-shrink: 0;
  transform: rotate(-180deg);
}
.collapse-btn.is-collapsed {
  transform: rotate(0deg);
}

.header-text {
  font-size: 14px;
  font-weight: 600;
  color: #2e3646;
}

.unit-badge {
  font-size: 12px;
  font-weight: 600;
  color: #2e3646;
  background: #f0f2f7;
  border-radius: 999px;
  padding: 3px 10px;
  white-space: nowrap;
}
.unit-badge--warning {
  color: #973c00;
  background: #fceedf;
}

/* ---------- Body ---------- */
.group-body {
  padding: 0 16px 16px 16px;
}

.warning-banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: #973c00;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 14px;
}
.warning-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.unit-list-label,
.table-label {
  font-size: 12.5px;
  color: #6b7280;
  margin-bottom: 8px;
}
.table-label {
  margin-top: 16px;
}

.unit-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.unit-tag {
  background: #f1f2f5;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 8px;
}

.ellipsis-dots {
  color: #2563eb;
}

/* =========================
   DIV TABLE
========================= */

.payment-table {
  width: 100%;

  border: 1px solid #d9e0e7;
  border-radius: 6px;

  overflow: hidden;
}

/*
  Instead of <table>, each row is a CSS Grid.

*/
.payment-row {
  display: grid;

  grid-template-columns:
    1fr
    1fr
    1fr
    1fr
    1fr;

  min-height: 30px;

  border-bottom: 1px solid #d9e0e7;
}

.payment-row:last-child {
  border-bottom: none;
}

.payment-row > div {
  display: flex;
  align-items: center;

  padding: 0 8px;

  border-right: 1px solid #d9e0e7;

  font-size: 12px;
}

.payment-row > div:last-child {
  border-right: none;
}

/* Header */

.payment-header {
  min-height: 29px;

  background: #f7f8fa;

  font-size: 12px;
  font-weight: 700;
  color: #6b7683;

  text-transform: uppercase;
}

.payment-header > div {
  justify-content: flex-start;
  white-space: nowrap;
}

.payment-header > div:nth-child(3),
.payment-header > div:nth-child(4),
.payment-header > div:nth-child(5) {
  justify-content: right;
}

/* Payment number */

.payment-number {
  justify-content: center;

  color: #1680ff;
  font-weight: 600;
}

/* Amount */

.payment-row > div:last-child {
  justify-content: flex-end;
}

.payment-row-content > div:nth-child(3),
.payment-row-content > div:nth-child(4),
.payment-row-content > div:nth-child(5) {
  display: flex;
  justify-content: flex-end;
}
</style>


// WEBPACK FOOTER //
// src/components/RentAdjustment/CustomComponent/PaymentPreview/PaymentGroup.vue